import { useState, useCallback, memo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { trackComponentRender } from '@/lib/performance-monitor';
import { RouteErrorBoundary } from "@/components/error/RouteErrorBoundary";
import { OverviewTab } from "@/components/dashboard/OverviewTab";
import SuccessionInsights from "./SuccessionInsights";
import { MySuccessionScoreOverlay } from "@/components/dashboard/MySuccessionScoreOverlay";
import { DocumentSelectionDialog } from "@/components/dashboard/DocumentSelectionDialog";
import { UploadDocumentDialog } from "@/components/dashboard/UploadDocumentDialog";

const Dashboard = memo(function Dashboard() {
  const renderStart = performance.now();
  const [showInsights, setShowInsights] = useState(false);
  const [showScoreOverlay, setShowScoreOverlay] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Open score overlay if navigated with ?overlay=score
  useEffect(() => {
    if (searchParams.get('overlay') === 'score') {
      setShowScoreOverlay(true);
      searchParams.delete('overlay');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);
  
  // Track dashboard render performance
  useEffect(() => {
    trackComponentRender('Dashboard', renderStart);
  }, [renderStart]);
  
  const handleDocumentSelect = useCallback((documentId: string) => {
    navigate(`/documents?selected=${documentId}`);
  }, [navigate]);
  
  const handleRisksClick = useCallback(() => {
    setShowInsights(true);
  }, []);
  
  const handleScoreClick = useCallback(() => {
    setShowScoreOverlay(true);
  }, []);
  
  const handleCloseInsights = useCallback(() => {
    setShowInsights(false);
  }, []);
  
  const handleCloseScoreOverlay = useCallback(() => {
    setShowScoreOverlay(false);
  }, []);
  
  return (
    <RouteErrorBoundary routeName="dashboard">
      <div className="space-y-6">
        <PageHeader 
          title="Dashboard" 
          description="Overview of your succession planning progress" 
        />
        
        <OverviewTab 
          onRisksClick={handleRisksClick} 
          onScoreClick={handleScoreClick} 
        />

        {/* Succession Insights Pop-up */}
        {showInsights && <SuccessionInsights onClose={handleCloseInsights} />}

        {/* mySuccession Score Overlay */}
        {showScoreOverlay && <MySuccessionScoreOverlay onClose={handleCloseScoreOverlay} />}

        {/* Document Selection Dialog */}
        <DocumentSelectionDialog 
          open={showDocumentDialog} 
          onOpenChange={setShowDocumentDialog} 
          onSelectDocument={handleDocumentSelect} 
        />

        {/* Upload Document Dialog */}
        <UploadDocumentDialog 
          open={showUploadDialog} 
          onOpenChange={setShowUploadDialog} 
        />
      </div>
    </RouteErrorBoundary>
  );
});

Dashboard.displayName = "Dashboard";

export default Dashboard;
